/*
 * @providesModule TournamentHighlightBorder
 */

import React, {useEffect, useRef, useState} from 'react';
import {View, StyleSheet, Animated, Platform} from 'react-native';
import GlobalEventEmitter from 'GlobalEventEmitter';
import SkillzImage from 'SkillzImage';

interface TournamentHighlightBorderProps {
  width: number;
  height: number;
  borderRadius?: number;
  tournamentId?: string;
  startAnimation?: boolean;
  borderColor?: string; // Allow custom color for debugging
  borderWidth?: number; // Allow custom border width
}

const TournamentHighlightBorder: React.FC<TournamentHighlightBorderProps> = ({
  width,
  height,
  borderRadius = 16,
  tournamentId,
  startAnimation = false,
  borderColor = '#f9eb0d', // Default yellow
  borderWidth = 5, // Default border width
}) => {
  // Calculate glow sizes
  const glowWidth = width * 0.24;
  const glowHeight = height * 0.24;

  // Animation values
  const topGlowAnim = useRef(new Animated.Value(0)).current;
  const bottomGlowAnim = useRef(new Animated.Value(0)).current;
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // Control visibility after 10 iterations

  const startAnimations = () => {
    topGlowAnim.setValue(0);
    bottomGlowAnim.setValue(0);
    setIsVisible(true); // Show component when animation starts

    // Create sequences with 10 iterations
    const topSequence = Animated.sequence(
      Array(10)
        .fill(null)
        .map(() =>
          Animated.sequence([
            Animated.timing(topGlowAnim, {
              toValue: 1,
              duration: 2250, // 2.25 seconds per cycle
              useNativeDriver: false,
            }),
            Animated.timing(topGlowAnim, {
              toValue: 0,
              duration: 0, // Instant reset
              useNativeDriver: false,
            }),
          ])
        )
    );

    const bottomSequence = Animated.sequence(
      Array(10)
        .fill(null)
        .map(() =>
          Animated.sequence([
            Animated.timing(bottomGlowAnim, {
              toValue: 1,
              duration: 2250, // 2.25 seconds per cycle
              useNativeDriver: false,
            }),
            Animated.timing(bottomGlowAnim, {
              toValue: 0,
              duration: 0, // Instant reset
              useNativeDriver: false,
            }),
          ])
        )
    );

    Animated.parallel([topSequence, bottomSequence]).start(() => {
      setIsVisible(false); // Hide component after 10 iterations
    });
  };

  useEffect(() => {
    if (!tournamentId) return;

    const subscription = GlobalEventEmitter.addListener(
      'StartTournamentGlow',
      (eventTournamentId: string) => {
        if (eventTournamentId === tournamentId) {
          setShouldAnimate(true);
        }
      }
    );

    return () => {
      subscription.remove();
    };
  }, [tournamentId]);

  useEffect(() => {
    if (shouldAnimate || startAnimation) {
      startAnimations();
      setShouldAnimate(false);
    }
  }, [shouldAnimate, startAnimation]);

  // Top glow: position and fade in/out at edges
  const topGlowLeft = topGlowAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, width - glowWidth],
  });

  const topGlowOpacity = topGlowAnim.interpolate({
    inputRange: [0, 0.15, 0.85, 1], // Fade in first 15%, fade out last 15%
    outputRange: [0, 1, 1, 0],
  });

  // Bottom glow: position and fade in/out at edges
  const bottomGlowLeft = bottomGlowAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [width - glowWidth, 0],
  });

  const bottomGlowOpacity = bottomGlowAnim.interpolate({
    inputRange: [0, 0.15, 0.85, 1], // Fade in first 15%, fade out last 15%
    outputRange: [0, 1, 1, 0],
  });

  return (
    <View
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: width,
        height: height - 5,
        borderRadius: borderRadius,
      }}
      pointerEvents='none'
    >
      {/* Static border - ALWAYS VISIBLE */}
      <View
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: width,
          height: height - (Platform.OS !== 'ios' ? 3 : 0),
          borderRadius: borderRadius,
          borderWidth: borderWidth, // Use prop for custom width
          borderColor: borderColor, // Use prop for custom color
        }}
      />

      {/* Top border glow (left to right) with fade in/out - HIDDEN after 10 iterations */}
      {isVisible && (
        <Animated.View
          style={{
            position: 'absolute',
            left: topGlowLeft,
            width: glowWidth,
            height: glowHeight,
            top: -glowHeight / 2 - 2,
            opacity: topGlowOpacity,
          }}
        >
          <SkillzImage
            source='templateHighlight.png'
            style={styles.glowImage}
            resizeMode='stretch'
          />
        </Animated.View>
      )}

      {/* Bottom border glow (right to left) with fade in/out - HIDDEN after 10 iterations */}
      {isVisible && (
        <Animated.View
          style={{
            position: 'absolute',
            left: bottomGlowLeft,
            width: glowWidth,
            height: glowHeight,
            bottom: -glowHeight / 2 - (Platform.OS !== 'ios' ? 4 : 6),
            opacity: bottomGlowOpacity,
          }}
        >
          <SkillzImage
            source='templateHighlight.png'
            style={styles.bottomGlowImage}
            resizeMode='stretch'
          />
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  glowImage: {
    width: '100%',
    height: '100%',
    tintColor: '#f9eb0d',
  },
  bottomGlowImage: {
    width: '100%',
    height: '100%',
    tintColor: '#f9eb0d',
    transform: [{scaleY: -1}],
  },
});

export default TournamentHighlightBorder;
